import { Avatar, Space, Button, Popconfirm } from "antd";
import { get_api, put_api, post_api, delete_api } from "api";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { startCase } from "lodash";
import { QuestionCircleOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/img/optom-logo-small.png";

const useBusinessUsersListHook = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [companyProfileList, setCompanyProfileList] = useState([]);
    const [companyProfileUnlisted, setCompanyProfileUnlisted] = useState([]);
    const [tabKey, setTabKey] = useState('1');
    const [unListedRowData, setUnListedRowData] = useState({});

    const getAllBusinessUsers = async () => {
        try {
            const { data } = await get_api("company-profile");
            setCompanyProfileList( data.companyProfiles ?? [] );
            const dataUnlistedUsers = await get_api("company-profile/unlistedProfiles");
            console.log('dataUnlistedUsers: ', dataUnlistedUsers);
         
            const transformedDataUnlistedUsers = {
                companyProfiles: dataUnlistedUsers?.data?.usersWithoutCompanyProfiles.map(profile => ({
                    ...profile,
                    ...profile?.companyProfileDetails,
                    id: profile.id,
                }))
            };
            console.log('transformedDataUnlistedUsers: ', transformedDataUnlistedUsers);

            setCompanyProfileUnlisted( transformedDataUnlistedUsers.companyProfiles ?? [] );

            setIsLoading(false);
            return data;
        } catch (err) {
            toast.error(err.message);
            setIsLoading(false);
        }
    };
    
    const loginAsUser = async (userData) => {
        try {
            setIsLoading(true);
            const userId = userData.userId ?? 0;
            const userSession = await post_api( "company-profile/login-as/"+userId );
            if( 200 !== userSession.status ){
                const errorMsg = userSession.message ?? "Failed to generate user session.";
                throw new Error(errorMsg);
            }
            
            //console.log("env: ", process.env);
            const clinicHome = `${process.env.REACT_APP_CLINIC_USER_HOME}`;
            //console.log("user session", userSession, clinicHome);
            const tokens = userSession.data.tokens;
            const companyProfile = JSON.stringify(userSession.data.companyProfile);
            const userProfile = JSON.stringify(userSession.data.user);
            const accessToken = tokens.access.token;
            const refreshToken = tokens.refresh.token;
            const targetUrl = new URL( clinicHome + "/receivesession/" );

            targetUrl.searchParams.append("access_token", accessToken);
            targetUrl.searchParams.append("refresh_token", refreshToken);
            targetUrl.searchParams.append("company_profile", companyProfile);
            targetUrl.searchParams.append("user_profile", userProfile);
            
            console.log( "TargetUrl: ", targetUrl.href );
            window.open( targetUrl.href, '_blank' ).focus();
            setIsLoading(false);
        } catch (err) {
            toast.error(err.message);
            setIsLoading(false);
        }
    };
    
    const approveUser = async (userData) => {
        try {
            setIsLoading(true);
            const profileId = userData._id ?? 0;
            const updateProfile = await put_api( "company-profile/"+profileId, {isVerified: true} );
            if( 200 !== updateProfile.status ){
                const errorMsg = updateProfile.message ?? "Failed to update company profile.";
                throw new Error(errorMsg);
            }
            toast.success("User approved!");
            // Trigger a reload of user listing
            getAllBusinessUsers();
            // NOTE: no need to run setIsLoading(false) - handled by getAllBusinessUsers()
        } catch (err) {
            toast.error(err.message);
            setIsLoading(false);
        }
    };
    
    const deleteUser = async (userData) => {
        try {
            setIsLoading(true);
            const profileId = userData._id ?? 0;
            const deleteProfile = await delete_api( "company-profile/"+profileId );
            if( 200 !== deleteProfile.status ){
                const errorMsg = deleteProfile.message ?? "Failed to delete user.";
                throw new Error(errorMsg);
            }
            toast.success("User successfully deleted!");
            // Trigger a reload of user listing
            getAllBusinessUsers();
            // NOTE: no need to run setIsLoading(false) - handled by getAllBusinessUsers()
        } catch (err) {
            toast.error(err.message);
            setIsLoading(false);
        }
    };
    
    const formatPhone = (phoneNumber) => {
        // 1. Clean the input: remove all non-digit characters.
        let cleaned = ('' + phoneNumber).replace(/\D/g, '');

        // Handle international code +61
        if (cleaned.startsWith('61')) {
            cleaned = '0' + cleaned.substring(2);
        }

        // 2. Validate the length. Australian numbers are 8 or 10 digits long (with area code).
        if (cleaned.length === 8) {
            return cleaned.replace(/(\d{4})(\d{4})/, '$1 $2');
        }
        
        if (cleaned.length !== 10) {
            return phoneNumber; // else return original
        }

        // 3. Check for mobile numbers (starting with 04).
        if (cleaned.startsWith('04')) {
            return cleaned.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3'); // Format: 04XX XXX XXX
        }

        // 4. Check for landline numbers. Area codes are 02, 03, 07, 08.
        if (cleaned.startsWith('02') || cleaned.startsWith('03') || cleaned.startsWith('07') || cleaned.startsWith('08')) {
             // Format: 0X XXXX XXXX
            return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3');
        }
        
        // else return original
        return phoneNumber;
    };

    useEffect(() => {
        setIsLoading(true);
        getAllBusinessUsers();
    }, []);

    const columns = [
        {
            title: "Company",
            dataIndex: "companyName",
            key: "companyName",
            render: (text, record) => (
                <div key={record._id} className="tbl-user-details">
                    <Avatar
                        icon={<UserOutlined />}
                        src={record?.companyLogo ? record?.companyLogo : logo}
                    />
                    <div>
                        <h6 className="m-0 text-primary text-capitalize">{text}</h6>
                        <div className="fields-wrapper">
                            {record?.contact?.website && (
                                <span
                                    className="m-0"
                                    onClick={() =>
                                        window.open(
                                            record?.contact?.website.includes("http")
                                                ? record?.contact?.website
                                                : `https://${record?.contact?.website}`,
                                            "_blank"
                                        )
                                    }
                                    style={{ cursor: "pointer" }}
                                >
                                    {record?.contact?.website}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: "Contact",
            dataIndex: "email",
            key: "email",
            render: (text, record) => {
                return(
                <div key={record._id}>
                    <div className="fields-wrapper">
                        {record?.contact?.email && (
                            <span className="mb-1">{record.contact.email}</span>
                        )}
                        {record?.email && (
                            <span className="m-0">{record.email}</span>
                        )}
                    </div>
                    <div className="fields-wrapper">
                        {record?.contact?.phone && (
                            <p className="m-0">{formatPhone(record.contact.phone)}</p>
                        )}
                    </div>
                </div>
            )},
            align: "center",
        },
        {
            title: "Plan",
            dataIndex: "pricingPlan",
            key: "pricingPlan",
            render: (text, record) => {
                const pricingPlan = record?.pricingPlan ?? null;
                const interval = record?.pricingPlan?.productIntervalType ?? null;
                const status = record?.pricingPlan?.subscriptionStatus ?? null;
                const cancelAt = record?.pricingPlan?.cancelAt ?? null;
                // Check if cancelled date is past
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Set time to midnight to only compare dates
                const todayTime = today.getTime();
                const isCancelled = ("canceled" === status) || ( (null !== cancelAt) && (cancelAt * 1000 <= todayTime) );
                return (
                    <div key={record._id}>
                        <div>
                            {!isCancelled
                                ? interval === "month"
                                    ? "Monthly"
                                    : interval === "year"
                                        ? "Yearly"
                                        : ""
                                : ""}
                        </div>
                        <span>
                            {(null !== cancelAt || "canceled" === status)
                                ? isCancelled
                                    ? "Cancelled"
                                    : "Pending-Cancel"
                                : (null === record?.pricingPlan)
                                    ? "Free"
                                    : (status !== "trialing")
                                        ? startCase(status)
                                        : "Free Trial"
                            }
                        </span>
                    </div>
                );
            },
            align: "center",
        },
        {
            title: "Total Shifts",
            dataIndex: "total_shifts",
            key: "total_shifts",
            render: (text, record) => (
                <div
                    key={record._id}
                    style={{ cursor: "pointer", fontWeight: "bold" }}
                    onClick={() => navigate(`/business-users/${record._id}/post-shift`)}
                >
                    {record?.post_shift_ids?.length}
                </div>
            ),
            align: "center",
        },
        {
            title: "Registered On",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (text, record) => (
                <div key={record._id}>
                    <div className="fields-wrapper">
                        {dayjs(text).format("DD-MM-YYYY")}
                    </div>
                    {!record?.isVerified ? (
                        <Popconfirm
                            title={"Approve "+record?.companyName+" account?"}
                            description="Are you sure you would like to approve this account?"
                            icon={
                                <QuestionCircleOutlined
                                    style={{
                                        color: "creen",
                                    }}
                                />
                            }
                            onConfirm={() => approveUser(record)}
                        >
                            <Button
                                className="action-btn action-approve-user"
                                title="Approve..."
                                icon={<span className="m-0">PENDING APPROVAL</span>}
                            />
                        </Popconfirm>
                    ) : null }
                </div>
            ),
            align: "center",
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Space size={[10, 0]} key={record._id}>
                    
                    <Popconfirm
                        title="Log in as User"
                        description="Would you like to log in as this user?"
                        icon={
                            <QuestionCircleOutlined
                                style={{
                                    color: "green",
                                }}
                            />
                        }
                        onConfirm={() => loginAsUser(record)}
                    >
                        <Button
                            className="action-btn"
                            title="Login as..."
                            icon={<i className="fas fa-sign-in-alt" />}
                        />
                    </Popconfirm>
                    <i
                        className="fas fa-edit"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            if(tabKey === "1") {
                                navigate(`/business-users/${record._id}/view`)
                            } else if(tabKey === "2") {
                                setUnListedRowData(record)
                            }
                        }}
                    />
                    <Popconfirm
                        title="Delete User"
                        description="Are you sure you would like to delete this user?"
                        icon={
                            <QuestionCircleOutlined
                                style={{
                                    color: "red",
                                }}
                            />
                        }
                        onConfirm={() => deleteUser(record)}
                    >
                        <Button
                            className="action-btn"
                            title="Delete User"
                            icon={<i className="fas fa-trash-alt text-danger" />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return {
        columns,
        companyProfileList,
        isLoading,
        companyProfileUnlisted,
        tabKey,
        setTabKey,
        unListedRowData,
        setUnListedRowData
    };
};

export default useBusinessUsersListHook;
